/**
 * useSubmitQuoteForm.js
 *
 * Orchestrates the full quote submission flow:
 *  1. Validate visible form fields
 *  2. Send Google logger payload
 *  3. Upload tax certificate (if tax exempt)
 *  4. Upload removal photos (if required)
 *  5. submitQuote — draft order + customer (awaited; only hard failure blocks success UI)
 *  6. Fire-and-forget: Zapier, dataLayer, Klaviyo, ads (not awaited)
 *  7. Clear localStorage and redirect to thank-you page
 *
 * On any error, sets submitErrors (array of strings) and opens the modal.
 */

import { useState, useCallback, useMemo } from 'react';
import isEmail from 'validator/lib/isEmail';

import { klavityoItems, enhancedConversionItems } from '../utils';
import { surfacingConfig, turfConfig, GOOGLE_LOGGER_URL, THANK_YOU_URL, submissionMessages } from '../constants';

import { trackKlaviyoEvent } from '../../utils/utils';
import { useConstructLineItems } from '../hooks/useConstructQuote';
import { calculateSurfacingDetails } from '../calculations/calculateSurfacing';
import { calculateInstallation } from '../calculations/calculateInstallation';
import { calculateLeadScore } from '../calculations/calculateLeadScore';

// then include in your server payload: { lineItems, ...rest }
import { submitQuote } from '../api/shopifyApi';

// Two celebration hops at 0.55s each, plus a small buffer before redirect.
const SUBMIT_CELEBRATION_MS = 1200;

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

function validateField(field, formData, taxFile) {
  const value = formData[field.name];

  if (field.required && !value) {
    return 'This field is required.';
  }

  if (field.type === 'email' && value) {
    const badDomains = ['.comm', '.ogr', '.vom'];
    if (!isEmail(value) || badDomains.some((d) => value.includes(d))) {
      return 'Please enter a valid email address.';
    }
  }

  if (field.type === 'tel' && value) {
    if (value.replace(/\D/g, '').length !== 10) {
      return 'Please enter a valid 10-digit phone number.';
    }
  }

  if (field.name === 'zip_code' && value) {
    if (value.replace(/\D/g, '').length < 5) {
      return 'Zip code must be at least 5 digits.';
    }
  }

  if (field.name === 'tax_certificate' && formData.tax_exempt === 'Yes') {
    const hasSelectedFile = !!taxFile?.file || !!taxFile?.uploaded?.fileGid;
    if (!hasSelectedFile) {
      return 'A tax exemption certificate is required for tax-exempt organizations.';
    }
  }

  // if (field.name === 'removal_photos') {
  //   const needsRemoval = formData.accommodations?.includes(
  //     'I need to remove old equipment (pictures required for quoting)'
  //   );
  //   const hasRemovalPhoto = !!removalPhotosFile?.uploaded?.fileGid || (removalPhotosFile?.file?.length ?? 0) > 0;
  //   if (needsRemoval && !hasRemovalPhoto) {
  //     return 'Removal photo is required when old equipment needs removal.';
  //   }
  // }

  return null;
}

// ---------------------------------------------------------------------------
// Analytics helpers
// ---------------------------------------------------------------------------

function pushToDataLayer(event, base, extra = {}) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...base, ...extra });
}


function closeRate(t) {
  if (t < 2000)   return 0.40;
  if (t < 5000)   return 0.38;
  if (t < 10000)  return 0.29;
  if (t < 25000)  return 0.15;
  if (t < 40000)  return 0.15;   // smoothed (10-25k & 25-40k are within noise)
  if (t < 60000)  return 0.08;
  if (t < 100000) return 0.07;
  return 0.04;
}


function publishShopifyAnalytics(eventName, data) {
  const publish = window.Shopify?.analytics?.publish;
  if (!publish) return;

  publish(eventName, data).catch((err) => {
    console.warn('Shopify analytics publish failed:', err);
  });
}

function buildQuoteEventData({
  converted_total,
  quote_value_adj,
  tierred_value,
  // conversion_value_a,
  // conversion_value_b,
  // conversion_value_c,
  draftOrderId,
  draftOrderName,
  formData,
  items,
  submissionLineItems,
  submitDerived,
  quoteData,
  isTaxExempt,
  expirationDate,
  analyticsData,
  leadScoreResult,
}) {
  return {
    quote_value: converted_total,
    adj_value: quote_value_adj,
    tierred_value: tierred_value,
    // conversion_value_a: conversion_value_a,
    // conversion_value_b: conversion_value_b,
    // conversion_value_c: conversion_value_c,
    form_id: 'rfq_form',
    quote_id: draftOrderId,
    draft_order_id: draftOrderId,
    draft_order_name: draftOrderName,
    currency: 'USD',
    quote_address_country: 'US',
    quote_email: formData.email.toLowerCase(),
    quote_phone_number: formData.phone_number,
    quote_first_name: formData.first_name.toLowerCase(),
    quote_last_name: formData.last_name.toLowerCase(),
    quote_address_street: formData.address_1,
    quote_address_street_2: formData.address_2,
    quote_address_city: formData.city.toLowerCase(),
    quote_address_region: formData.state.toLowerCase(),
    quote_address_postal_code: formData.zip_code,
    formatted_items: enhancedConversionItems(items),
    quote_items: items,
    quote_line_items: submissionLineItems,
    buying_for: formData.select_buying_for === 'Other (Please Specify)' ? formData.specific_buying_for : formData.select_buying_for,
    time_frame: formData.timeframe_select,
    budget: formData.budget_select,
    quote_installation: submitDerived.hasInstallRequest,
    quote_surfacing: submitDerived.hasSurfacingRequest,
    customer_id: quoteData?.customerId ?? null,
    email_opt_in: formData.email_opt_in === 'Yes',
    sms_opt_in: formData.sms_opt_in === 'Yes',
    tax_exempt: isTaxExempt,
    expiration_date: expirationDate,
    elevar_data: analyticsData.elevar_params,
    ...analyticsData,
    lead_score: leadScoreResult.score,
    shopper_id: quoteData?.customerId ?? null,
  };
}

function fireKlaviyo(eventData) {
  try {
    if (typeof window === 'undefined') return;
    const learnq = window._learnq || [];
    const items = eventData.quote_items;

    const identifyPayload = {
      ...(eventData.quote_email && { $email: eventData.quote_email }),
      ...(eventData.quote_first_name && { $first_name: eventData.quote_first_name }),
      ...(eventData.quote_last_name && { $last_name: eventData.quote_last_name }),
      ...(eventData.quote_phone_number && { $phone_number: eventData.quote_phone_number }),
    };
    if (Object.keys(identifyPayload).length > 0) {
      learnq.push(['identify', identifyPayload]);
    }

    trackKlaviyoEvent('Quote Submitted', {
      $value: eventData.quote_value,
      customer_properties: {
        $email: eventData.quote_email,
        $first_name: eventData.quote_first_name,
        $last_name: eventData.quote_last_name,
        $phone_number: eventData.quote_phone_number,
        $address1: eventData.quote_address_street,
        $address2: eventData.quote_address_street_2,
        $city: eventData.quote_address_city,
        $zip: eventData.quote_address_postal_code,
        $region: eventData.quote_address_region,
        $country: 'USA',
      },
      properties: {
        $value: eventData.quote_value,
        QuoteExpiration: eventData.expiration_date,
        Categories: items.flatMap((i) => i.collections || []),
        ItemNames: items.map((i) => i.title),
        Brands: items.map((i) => i.brand),
        'Buying For': eventData.buying_for,
        'Time Frame': eventData.time_frame,
        Items: klavityoItems(items),
        email_opt_in: eventData.email_opt_in,
        InitialLeadScore: eventData.lead_score ?? null,
      },
    });
  } catch (e) {
    console.warn('Klaviyo tracking failed:', e);
  }
}

function postLog(url, label, body, options = {}) {
  const fetchOptions = {
    method: 'POST',
    body,
    ...options,
  };
  if (!options.headers) {
    delete fetchOptions.headers;
  }
  return fetch(url, fetchOptions).catch((err) => {
    console.warn(`${label} failed:`, err);
  });
}


// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------



const useSubmitQuoteForm = (items, formData, taxFile, removalPhotosFile, totalPrice, expirationDate, analyticsData) => {
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [submitErrors, setSubmitErrors] = useState([]);
  const [submissionStep, setSubmissionStep] = useState(null);

  const selectedState = formData.state;
  
  const submitDerived = useMemo(() => {
    const hasInstallRequest = formData.quote_installation === 'Yes' ? 'Yes' : 'No';
    const hasPIPProduct = items.some((item) => item.productId == surfacingConfig.surfacing_product_id);
    const hasTurfProduct = items.some((item) => item.productId == turfConfig.turf_product_id);
    const hasSurfacingRequest = (formData.surfacing_request === 'Yes' || hasPIPProduct || hasTurfProduct) ? 'Yes' : 'No';

    const hasPourInPlace = (hasPIPProduct || formData.surfacing_type == 'Pour in Place Surfacing') ? 'Yes' : 'No';
    const hasTurf = (hasTurfProduct || formData.surfacing_type == 'Artificial Turf') ? 'Yes' : 'No';
    const hasEWF = formData.surfacing_type == 'Engineered Wood Fiber';

    const needsRemoval = formData.accommodations?.includes(
      'I need to remove old equipment (pictures required for quoting)'
    );

    

    return {
      hasInstallRequest,
      hasSurfacingRequest,
      hasPIPProduct,
      hasTurfProduct,
      hasPourInPlace,
      hasTurf,
      hasEWF,
      needsRemoval,
    };
  }, [formData.quote_installation, formData.surfacing_request, formData.surfacing_type, formData.accommodations, items]);

  const installationDetails = useMemo(() => {
    if (submitDerived.hasInstallRequest !== 'Yes') {
      return {
        totalInstallationCost: 0,
        totalInstallationPrice: 0,
        allItemsHaveInstall: true,
        installVariantGid: null,
        installVariantPrice: null,
        installDiscountAmount: 0,
        installationMinimumMet: false,
      };
    }
    return calculateInstallation(items, selectedState);
  }, [items, selectedState, submitDerived.hasInstallRequest]);

  const {
    totalInstallationCost,
    totalInstallationPrice,
    allItemsHaveInstall,
    installVariantGid,
    installDiscountAmount,
    
  } = installationDetails ?? {};

  const installationLineItems = useMemo(() => {
    const wantsInstall = formData?.quote_installation === 'Yes';
    if (!wantsInstall) return [];
    if (!installVariantGid) return [];

    const li = {
      variantId: installVariantGid,
      quantity: 1,
      customAttributes: [
        { key: 'Quote', value: 'Installation' },
        ...(!allItemsHaveInstall
          ? [{ key: 'Installation', value: 'Needs custom installation quote' }]
          : []),
      ],
    };

    if ((installDiscountAmount ?? 0) > 0) {
      li.appliedDiscount = {
        valueType: 'FIXED_AMOUNT',
        title: 'Installation - Discount',
        value: Math.round(installDiscountAmount),
      };
    }

    return [li];
  }, [formData?.quote_installation, installVariantGid, installDiscountAmount, allItemsHaveInstall]);

  
  const surfacingDetails = useMemo(() => {
    if (submitDerived.hasSurfacingRequest !== 'Yes') {
      return { kind: null, lineItems: [], totalCost: null, totalPrice: null };
    }
    return calculateSurfacingDetails({
      items,
      surfacingType: formData?.surfacing_type ?? '',
      surfacingRequest: formData?.surfacing_request ?? '',
      // Raw strings ok ("4 Feet"); calculateSurfacing uses parseNumeric / positiveParsed
      criticalFallHeight: formData?.critical_fall_height,
      squareFootage: formData?.surfacing_square_footage,
    });
  }, [
    items,
    formData?.surfacing_type,
    formData?.surfacing_request,
    formData?.critical_fall_height,
    formData?.surfacing_square_footage,
    submitDerived.hasSurfacingRequest,
  ]);

  

  const surfacingLineItems = surfacingDetails?.lineItems ?? [];

  const lineItems = useConstructLineItems(items, selectedState);
  const submissionLineItems = useMemo(
    () => [
      ...(lineItems ?? []),
      ...(installationLineItems ?? []),
      ...(surfacingLineItems ?? []),
    ],
    [lineItems, installationLineItems, surfacingLineItems]
  );

  // console.log('lineItems', lineItems, 'installVariantGid', installVariantGid, 'installDiscountAmount', installDiscountAmount);

  // -------------------------------------------------------------------------
  // Validation
  // -------------------------------------------------------------------------

  const validateForm = useCallback(
    (visibleFields) => {
      const errors = {};
      let hasErrors = false;
      let firstErrorName = null;

      visibleFields.forEach((field) => {
        const error = validateField(field, formData, taxFile);
        if (error) {
          errors[field.name] = error;
          hasErrors = true;
          if (!firstErrorName) firstErrorName = field.name;
        }
      });

      if (!formData.terms_of_service?.includes('I accept the terms of use')) {
        errors.terms_of_service = 'You must accept the terms of use.';
        hasErrors = true;
        if (!firstErrorName) firstErrorName = 'terms_of_service';
      }

      setFormErrors(errors);
      return { isValid: !hasErrors, errors, firstErrorName };
    },
    [formData, taxFile]
  );

  // -------------------------------------------------------------------------
  // Submit
  // -------------------------------------------------------------------------

  const submitForm = useCallback(
    async (visibleFields) => {
      const { isValid, firstErrorName } = validateForm(visibleFields);
      if (!isValid) {
        if (firstErrorName) {
          document
            .querySelector(`[name="${firstErrorName}"]`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }

      setIsSubmitting(true);
      window.document.dispatchEvent(new CustomEvent("theme:loading:start"));
      setSubmitErrors([]);

      const submissionLabels =
        submissionMessages[Math.floor(Math.random() * submissionMessages.length)];

      setSubmissionStep({ messages: submissionLabels, progress: 8 });
      const converted_total = totalPrice / 100;

      

      try {
        
        const quoteID = localStorage.getItem('quoteId');
        const isTaxExempt = formData.tax_exempt === 'Yes';
        
        // setSubmissionStep({ label: getSubmissionLabel(), progress: 22 });
        

        // ------------------------------------------------------------------
        // Step 3: Upload tax certificate (sets wg_fields.tax_cert metafield)
        // ------------------------------------------------------------------
        // taxFile and removalPhotosFile are useFileUpload handles. Their
        // uploadIfNeeded() returns the cached `uploaded` payload if the
        // interactive upload already happened, otherwise uploads the held
        // file and returns the parsed response. Returns null if there's
        // no file. Throws on error; err.uploadErrors carries the array
        // from the API.
        let taxCertFileGid = null;
        let taxCertFileUrl = null;
        let taxCertCustomerGid = null;

        if (isTaxExempt) {
          try {
            const cert = await taxFile.uploadIfNeeded();
            if (cert) {
              taxCertFileGid = cert.fileGid ?? null;
              taxCertFileUrl = cert.fileUrl ?? null;
              taxCertCustomerGid = cert.customerGid ?? null;
            }
          } catch (err) {
            setSubmitErrors(err?.uploadErrors || [err?.message || 'Tax certificate upload failed.']);
            setSubmissionStep(null);
            setShowModal(true);
            return;
          }
        }

        // ------------------------------------------------------------------
        // Step 4: Upload removal photos
        // ------------------------------------------------------------------
        let removalPhotosFileUrl = null;
        let removalPhotosFileGid = null;
        const needsRemoval = submitDerived.needsRemoval;

        if (needsRemoval) {
          try {
            const removal = await removalPhotosFile.uploadIfNeeded();
            if (removal) {
              removalPhotosFileGid = removal.fileGid ?? null;
              removalPhotosFileUrl = removal.fileUrl ?? null;
            }
          } catch (err) {
            setSubmitErrors(err?.uploadErrors || [err?.message || 'Removal photos upload failed.']);
            setSubmissionStep(null);
            setShowModal(true);
            return;
          }
        }

        // const installationBoolean = ['willygoat', 'yes'].includes(formData.quote_installation?.toLowerCase()) ? 'Yes' : 'No';
        // const hasInstall = installationBoolean === 'Yes';
        // const hasSurfacing = formData.surfacing_request == 'Yes';
        // const hasPIPProduct = items.some(item => item.productId == surfacingConfig.surfacing_product_id);
        // const hasTurfProduct = items.some(item => item.productId == turfConfig.turf_product_id);
        // const hasPourInPlace = hasPIPProduct || (formData.surfacing_type == 'Pour in Place Surfacing') ? 'Yes' : 'No';
        // const hasTurf = hasTurfProduct || (formData.surfacing_type == 'Artificial Turf') ? 'Yes' : 'No';
        // const hasEWF = formData.surfacing_type == 'Engineered Wood Fiber';
        // const installationBoolean = submitDerived.installationBoolean;
        const hasInstall = submitDerived.hasInstallRequest === 'Yes';
        const hasSurfacing = submitDerived.hasSurfacingRequest === 'Yes';
        const hasPourInPlace = submitDerived.hasPourInPlace === 'Yes';
        const hasTurf = submitDerived.hasTurf === 'Yes';
        let quote_value_adj = Math.min(converted_total * 0.2, 50000); // Simplified adjustment calculation

        let tiered_pct;
        if (converted_total <= 5000) {
          tiered_pct = 0.20;
        } else if (converted_total <= 10000) {
          tiered_pct = 0.20 + ((converted_total - 5000) / 5000) * 0.05;
        } else if (converted_total <= 25000) {
          tiered_pct = 0.25;
        } else if (converted_total <= 40000) {
          tiered_pct = 0.25 - ((converted_total - 25000) / 15000) * 0.05;
        } else if (converted_total <= 60000) {
          tiered_pct = 0.20 - ((converted_total - 40000) / 20000) * 0.05;
        } else if (converted_total <= 80000) {
          tiered_pct = 0.15 - ((converted_total - 60000) / 20000) * 0.02;
        } else {
          tiered_pct = 0.13;
        }
        const tierred_value = Math.min(converted_total * tiered_pct, 25000);
        // console.log('tierred_value', tierred_value);

        
        /*
        const conversion_value_a = converted_total * closeRate(converted_total);
        const p = 0.03 + 0.40 * Math.exp(-converted_total / 22000);
        const conversion_value_b = converted_total * p;

        const isResidential = formData.select_buying_for === 'Private Home';
        const budgetStated =
          !!formData.budget_select && formData.budget_select !== "I haven't established a budget";
        const k = 0.72;                                   // global calibration constant
        let conversion_value_c = (0.03 + 0.40 * Math.exp(-converted_total / 22000)) * converted_total;
        if (isTaxExempt) conversion_value_c *= 2.2;             // self-identified exempt closes ~38% vs ~20%
        if (budgetStated) conversion_value_c *= 1.3;             // stated $ range closes ~33% vs ~21%
        if (isResidential) conversion_value_c *= 0.4;             // private-home / residential are mostly tire-kickers (~0%)
        // if (isReseller)      conversion_value_c *= 0.8;             // contractors price-shop (volatile)
        conversion_value_c = k * conversion_value_c;
        if (converted_total < 100) conversion_value_c = 0;
        */
        


        // analyticsData is computed outside submit (memoized) and passed in

        // ------------------------------------------------------------------
        // Step 5: Create Shopify Draft Order
        //   Finds/updates the customer, sets tax cert metafields, and creates
        //   the draft order — all in one server-side request.
        // ------------------------------------------------------------------
        setSubmissionStep((prev) => ({ ...prev, progress: 68 }));

        const leadScoreResult = calculateLeadScore(formData, items, totalPrice);
        // const leadScore = leadScoreResult.score;
        console.log('leadScoreResult', leadScoreResult);

        
        const { data: quoteData, errors: quoteErrors } = await submitQuote({
          formData,
          items: submissionLineItems,
          quoteId: quoteID,
          expirationDate: expirationDate?.toISOString?.() ?? null,
          taxCertFileGid,
          taxCertFileUrl,
          customerGid: taxCertCustomerGid,
          removalPhotosFileUrl,
          removalPhotosFileGid,
          analyticsData,
          initialLeadScore: leadScoreResult,
          // totalInstallationCost,
          // totalInstallationPrice,
          // allItemsHaveInstall,
        });

        

        const loggerBody = JSON.stringify({
          additional_data: { ...formData, ...analyticsData },
          initial_lead_score: leadScoreResult.score,
          lead_score_breakdown: leadScoreResult.breakdown,
          items: items,
          quoteId: quoteData?.draftOrderName || quoteID,
          expirationDate: expirationDate?.toISOString?.() ?? null,
          cart_total_cents: totalPrice ?? null,
          cart_total: converted_total ?? null,
          submissionDate: new Date().toISOString(),
          
          ...(quoteErrors && { errors: quoteErrors }),
          // customer_id: null,
          // draft_order_id: null,
          // draft_order_name: null,
        });
        postLog(GOOGLE_LOGGER_URL, 'Google logger', loggerBody, { headers: { 'Content-Type': 'application/json' } });
        
      
        if (quoteErrors) {
          setSubmitErrors(quoteErrors);
          setSubmissionStep(null);
          setShowModal(true);
          window.document.dispatchEvent(new CustomEvent("theme:loading:end"));
          return;
        }
        setSubmissionStep((prev) => ({ ...prev, progress: 85 }));

        // One canonical payload for EVERYTHING (api + logger + zapier).
        // - `items` stays as DraftOrderLineItemInput shape for /api/quote
        // - `cart_items` preserves the original cart item objects for analytics/webhooks
        /*
        const updatedLoggerBody = {
          ...loggerBody,
          ...formData,
          ...analyticsData,
          converted_total: converted_total,
          customer_id: quoteData?.customerId ?? null,
          draft_order_id: quoteData?.draftOrderId ?? null,
          draft_order_name: quoteData?.draftOrderName ?? null,
        };
        postLog(ZAPIER_WEBHOOK_URL, 'Zapier webhook', updatedLoggerBody, { mode: 'no-cors' });
        */
        /*
        const payload = {
          // Server quote request (expected fields)
          additional_data: { ...formData, ...analyticsData },
          ...formData,
          ...analyticsData,
          items,
          quoteId: quoteID,
          expirationDate: expirationDate?.toISOString?.() ?? null,
          cart_total_cents: totalPrice ?? null,
          cart_total: converted_total ?? null,
          converted_total: converted_total ?? null,
          submissionDate: new Date().toISOString(),

          // Extra fields for downstream consumers (logger/zapier/debug)
          // cart_items: items,
          // tax_exemption_data: taxFile?.base64 ?? null,

          // Include server response identifiers once available
          customer_id: quoteData?.customerId ?? null,
          draft_order_id: quoteData?.draftOrderId ?? null,
          draft_order_name: quoteData?.draftOrderName ?? null,
        };
        const body = JSON.stringify(payload);
        postLog(ZAPIER_WEBHOOK_URL, 'Zapier webhook', body, { mode: 'no-cors' });
        */


        

        const inst_conv_p =
          installationDetails.totalInstallationPrice === undefined ? 0 : installationDetails.totalInstallationPrice;
        const inst_conv_p_adj =
          installationDetails.totalInstallationPrice === undefined? 0 : installationDetails.totalInstallationPrice * 0.05;

        const surfacingTotalPrice = surfacingDetails?.totalPrice ?? 0;
        const pip_prc = surfacingDetails?.kind === 'pip' ? surfacingTotalPrice : 0;
        const turf_prc = surfacingDetails?.kind === 'turf' ? surfacingTotalPrice : 0;
        const pip_prc_adj = pip_prc * 0.01;
        const turf_prc_adj = turf_prc * 0.01;

        // console.log('quoteData', quoteData?.draftOrderName, 'quoteID', quoteID);
        const draftOrderId = quoteData?.draftOrderId?.split('/').pop() || quoteID;
        const draftOrderName = quoteData?.draftOrderName || quoteData?.draftOrderId?.split('/').pop() || quoteID;

        const eventData = buildQuoteEventData({
          converted_total,
          quote_value_adj,
          tierred_value,
          // conversion_value_a,
          // conversion_value_b,
          // conversion_value_c,
          draftOrderId,
          draftOrderName,
          formData,
          items,
          submissionLineItems,
          submitDerived,
          quoteData,
          isTaxExempt,
          expirationDate,
          analyticsData,
          leadScoreResult,
        });

        try {
          pushToDataLayer('rfq_submission', eventData);

          if (hasInstall) {
            pushToDataLayer('rfq_installation', {
              ins_conv_val: inst_conv_p,
              ins_conv_val_adj: inst_conv_p_adj,
            });
          }

          if (hasSurfacing && hasPourInPlace) {
            pushToDataLayer('rfq_surfacing', {
              pip_conv_val: pip_prc,
              pip_conv_val_adj: pip_prc_adj,
            });
          } else if (hasSurfacing && hasTurf) {
            pushToDataLayer('rfq_surfacing', {
              pip_conv_val: turf_prc,
              pip_conv_val_adj: turf_prc_adj,
            });
          }

          fireKlaviyo(eventData);

          window._conv_q = window._conv_q || [];
          window._conv_q.push(['triggerConversion', '10049594']);

          publishShopifyAnalytics('willygoat:quote_submitted', eventData);
        } catch (e) {
          console.warn('Background analytics failed:', e);
        }
        setSubmissionStep((prev) => ({ ...prev, progress: 95 }));

        // ------------------------------------------------------------------
        // Step 6: Clear state, celebrate, then redirect
        // ------------------------------------------------------------------
        setSubmissionStep((prev) => ({ ...prev, progress: 100, label: 'Quote submitted!' }));

        localStorage.removeItem('quoteId');
        window.document.dispatchEvent(new CustomEvent("theme:loading:end"));

        if (formData.email !== 'joel@willygoat.com' && formData.email !== 'joel+test123@willygoat.com') {
          localStorage.removeItem('quoteItems');
          localStorage.removeItem('quoteFormData');
          localStorage.removeItem('quoteFormUploads');

          const thankYouUrl = (() => {
            try {
              const u = new URL(THANK_YOU_URL, window.location.origin);
              if (quoteData?.draftOrderName) {
                u.searchParams.set('draftOrderName', quoteData.draftOrderName);
              }
              return u.toString();
            } catch {
              // Fallback: if THANK_YOU_URL is somehow malformed, still redirect.
              if (!quoteData?.draftOrderName) return THANK_YOU_URL;
              const sep = THANK_YOU_URL.includes('?') ? '&' : '?';
              return `${THANK_YOU_URL}${sep}draftOrderName=${encodeURIComponent(quoteData.draftOrderName)}`;
            }
          })();

          await wait(SUBMIT_CELEBRATION_MS);
          window.location.href = thankYouUrl;
        } else {
          console.log('eventData', eventData);
        }
      } catch (err) {
        console.error('Quote submission error:', err);
        setSubmitErrors([err.message || 'An unexpected error occurred. Please try again.']);
        setSubmissionStep(null);
        setShowModal(true);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      formData,
      taxFile,
      removalPhotosFile,
      items,
      totalPrice,
      expirationDate,
      analyticsData,
      validateForm,
      submitDerived,
      submissionLineItems,
      installationDetails,
      surfacingDetails,
    ]
  );

  return {
    formErrors,
    setFormErrors,
    submitForm,
    validateForm,
    installationDetails,
    surfacingDetails,
    baseLineItems: lineItems,
    submissionLineItems,
    // surfacingLineItems,
    // installationLineItems,
    isSubmitting,
    submissionStep,
    showModal,
    setShowModal,
    submitErrors,
  };
};

export default useSubmitQuoteForm;
